import { majorRoutes } from '../../constants/majorRoutes';
import { useFormUnsavedChanges } from '../../hooks/useFormUnsavedChanges';
import { ApiResponse } from '../../services/apiService';
import { React, GridColDef, useState, useEffect, useParams, useNavigate, Typography, Box, Tabs, Tab, Grid2, IconButton, ArrowBackIosIcon, Dayjs, ClientNotes, Chip } from '../../utils/globalImports';
import { dayjs, AppLayout, EditCalendarIcon, ViewClientProjects, CustomToggleButton, Toaster, CustomDataGrid, CustomButton, CustomTextField, CustomDropdown, CustomRadioButton, CustomAddContactModal, useApi, validationMessages, FilterCard, CustomDataTable, ContactType, ContactTypeId, AddNotes } from '../../utils/globalImports';
import { useConfirmCloseDialog } from '../../contexts/ConfirmCloseDialogContext';
import { headingStyles } from '../../assets/styles/CommonStyles';

interface Option {
    value: string | number;
    text: string;
}

interface Client {
    clientId?: number;
    clientName: string;
    clientNumber: string;
    categoryId: number;
    regionId: number;
    status?: number;
    notes?: string;
    clientEmail?: string;
    clientPhone?: string;
    createdId?: number;
    updatedId?: number;
    contacts?: Contact[];
    notesAndComments?: NotesAndComments[];
}

interface Contact {
    contactId?: number;
    clientId?: number,
    contactType: number;
    firstName: string;
    lastName: string;
    mobileNo: string;
    office?: string;
    isPrimary: boolean;
    emailAddress: string;
    status?: number;
    createdId?: number;
    updatedId?: number;
    clientName?: string
}

interface NotesAndComments {
    clientId?: number,
    clientNoteId?: number;
    description: string;
    createdId?: number;
    createdName?: string;
    updatedId?: number;
    updatedName?: string;
    createdAt?: Dayjs;
    updatedAt?: Dayjs
    status?: number;
}


export interface ClientProject {
    projectId?: number;
    projectNumber: string;
    projectName: string;
    startDate: string;
    endDate: string;
    status: number | string;
}

const tabStyles = {
    textTransform: "none",
    fontWeight: 500,
    flex: 1, // Ensures even spacing
    minWidth: "auto",
    maxWidth: "100%",
    fontSize: "16px",
    color: "#1e66ab", // Customizable text color
    borderBottom: "2px solid #d3d3d3", // Underline for all tabs
    "&.Mui-selected": {
        backgroundColor: "#1e66ab", // Customizable active tab color
        color: "white",
        borderRadius: "8px 8px 0 0",
    },
};

const AddClient: React.FC = () => {
    const navigate = useNavigate();
    const { execute } = useApi();
    const [selectedStatus, setSelectedStatus] = useState<any>([1]);//added
    const [category, setCategory] = useState<Option[]>([]);
    const [region, setRegion] = useState<Option[]>([]);
    const [open, setOpen] = useState(false);  // State to control modal visibility
    const [openModal, setModal] = useState(false);
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const [isOn, setIsOn] = useState<boolean>();  // State to manage the toggle button's "on/off" state
    const [dialogOpen, setDialogOpen] = useState(false);  // State to manage confirm dialog "open/close" state
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [contactDataList, setContactDataList] = useState<any[]>([]);
    const [projectData, setProjectData] = useState<ClientProject[]>([]);
    // const [contactNotesList, setNotesDataList] = useState<any[]>([]);
    const [selectedContact, setSelectedContact] = useState<any | null>(null); // Holds data of the contact being edited or null for add
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [activeTab, setActiveTab] = React.useState(1);

    const { clientId } = useParams<{ clientId?: string }>(); // Get clientId from URL
    const props = { clientId, showContactTypeDropdown: true, title: 'Client Contact', actionText: 'Add Contact', searchbarText: validationMessages.clientContact_searchText }

    const userId = Number(localStorage.getItem("userId"));

    const [formData, setFormData] = useState({
        clientNumber: '',
        clientName: '',
        clientCategory: -1,
        clientRegion: -1,
        status: 'Active',
    });

    const [initialFormData, setInitialFormData] = useState({
        clientNumber: '',
        clientName: '',
        clientCategory: -1,
        clientRegion: -1,
        status: 'Active',
    });

    const [errors, setErrors] = useState({
        clientNumber: '',
        clientName: '',
        clientCategory: '',
        clientRegion: '',
    });

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const { showConfirmCloseDialog } = useConfirmCloseDialog();

    useEffect(() => {

        if (clientId) {
            fetchClient();
            fetchProjects();
        } else {
            setFormData({
                clientNumber: '',
                clientName: '',
                clientCategory: -1,
                clientRegion: -1,
                status: 'Active',
            });
        }
        fetchCategory(); // Fetch Category on component mount
        fetchRegion(); // Fetch Region on component mount

    }, [clientId]);

    const sortOptions = [
        { value: "name", text: "Contact Name" },
        { value: "email", text: "Email" },
    ];

    // Delay function
    const delay = (ms: number | undefined) => new Promise(resolve => setTimeout(resolve, ms));
    // fetch ClientCategory
    const fetchCategory = async () => {
        const { data } = await execute('get', '/LookUp/GetCategories');
        if (data && Array.isArray(data)) {
            const categoryOptions = data
                .filter((category: any) => category.status === true) // Filter active category
                .map((category: any) => ({
                    value: category.categoryId,  // Ensure `id` exists in the response
                    text: category.categoryName, // Ensure `name` exists in the response
                }))
                .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' })); // Case-insensitive sorting;
            setCategory(categoryOptions);
        }
    }

    // fetch ClientRegion
    const fetchRegion = async () => {
        const { data } = await execute('get', '/LookUp/GetRegions');
        if (data && Array.isArray(data)) {
            const regionOptions = data
                .filter((region: any) => region.status === true) // Filter active region
                .map((region: any) => ({
                    value: region.regionId,  // Ensure `id` exists in the response
                    text: region.regionName, // Ensure `name` exists in the response
                }))
                .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' })); // Case-insensitive sorting;
            setRegion(regionOptions);
        }
    }

    // Fetch client
    const fetchClient = async () => {
        const { data } = await execute<Client>('get', `/Client/GetClient/${clientId}`);
        if (data) {
            setSelectedClient(data)
            // Update formData with fetched data
            const formData = {
                clientNumber: data.clientNumber || '',
                clientName: data.clientName || '',
                clientCategory: data.categoryId || -1,
                clientRegion: data.regionId || -1,
                status: data.status === 1 ? 'Active' : 'Inactive',
            };
            setFormData(formData);
            setInitialFormData(formData);
            setContactDataList(data.contacts ?? [])
            // setNotesDataList(data.notesAndComments ?? [])
            showGrid(data.contacts?.filter((contact) => contact.status === 1)); // Send updated list to showGrid
        }
        else {
            navigate('/404');  // Redirect to a Not Found page
        }
    };

    // fetch All Contacts 
    const fetchAllContacts = async () => {
        const { data } = await execute<Contact[]>('get', `/Client/GetAllClientContacts/${clientId}`);
        if (data && Array.isArray(data)) {
            setContactDataList(data)
            showGrid(data, selectedStatus); // Send updated list to showGrid with CurrentStaus
        }
    }
    // fetch Client Project 
    const fetchProjects = async () => {
        const { data } = await execute<ClientProject[]>('get', `/Project/GetProjectByClientID/${clientId}`);
        if (data) {
            const projects = data.map((project) => {
                const { projectId,
                    projectNumber,
                    projectName,
                    startDate,
                    endDate,
                    status
                } = project;

                return {
                    id: projectId,
                    projectNumber,
                    projectName,
                    startDate: dayjs(startDate).format("DD/MM/YYYY"),
                    endDate: dayjs(endDate).format("DD/MM/YYYY"),
                    status: status === 1 ? 'Active' : 'Inactive'
                }
            })
            setProjectData(projects);
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        // For other inputs like text fields, handle them normally
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    // validate client form field
    const validate = () => {
        let isValid = true;
        const errors = { clientNumber: '', clientName: '', clientCategory: '', clientRegion: '' };

        if (!formData.clientNumber || !formData.clientNumber.trim()) {
            errors.clientNumber = validationMessages.common_required("Client number");
            isValid = false;
        }
        else if (formData.clientNumber.length > 20) {
            errors.clientNumber = validationMessages.common_maxLength("Client number", 20);
            isValid = false;
        }
        else if (/[^a-zA-Z0-9\s]/.test(formData.clientNumber)) {
            errors.clientNumber = validationMessages.common_invalidStringSpecialCharacters("Client number")
            isValid = false;
        }

        if (!formData.clientName || !formData.clientName.trim()) {
            errors.clientName = validationMessages.common_required("Client name");
            isValid = false;
        }
        else if (/[^a-zA-Z0-9\s]/.test(formData.clientName)) {  // Checks if clientName contains any number
            errors.clientName = validationMessages.common_invalidStringAplamumeric("Client name")
            isValid = false;
        }
        else if (formData.clientName.length > 100) {
            errors.clientName = validationMessages.common_maxLength("Client name", 100);
            isValid = false;
        }

        if (formData.clientCategory === -1) {
            errors.clientCategory = validationMessages.common_required("Client category");
            isValid = false;
        }

        if (formData.clientRegion === -1) {
            errors.clientRegion = validationMessages.common_required("Client region");
            isValid = false;
        }

        setErrors(errors);
        return isValid;
    };

    // handle add or edit client
    const handleClientSubmit = async () => {
        if (validate()) {
            let response: ApiResponse<any>;
            if (clientId) {
                const updatedClient = {
                    ...selectedClient, // Keep the original user data
                    clientNumber: formData.clientNumber,
                    clientName: formData.clientName,
                    categoryId: formData.clientCategory,
                    regionId: formData.clientRegion,
                    status: formData.status === 'Active' ? 1 : 0,
                    updatedId: userId,
                    // contacts: contactDataList,
                    // notesAndComments: contactNotesList
                };

                response = await execute('put', '/Client/UpdateClient', updatedClient);
            } else {
                // Adding a new client
                const client: Client = {
                    clientNumber: formData.clientNumber,
                    clientName: formData.clientName,
                    categoryId: formData.clientCategory,
                    regionId: formData.clientRegion,
                    clientEmail: '',
                    clientPhone: '',
                    notes: '',
                    createdId: userId,
                    // contacts: contactDataList,
                    // notesAndComments: contactNotesList
                };
                response = await execute('post', '/Client/AddClient', client);
            }

            if (response!) {
                if (response.error) {
                    // If there's an error in the response, display the error message
                    setToast({ open: true, message: response.error, type: "error" });
                } else {
                    setInitialFormData(formData);
                    if (typeof response.data === 'object' && response.data.clientId) {
                        // If the response is successful, display the success message
                        // console.log(response.data.clientId)
                        setToast({ open: true, message: validationMessages.client_InsertResponse_Text, type: "success" });
                        navigate(`/client/add-client/${response.data.clientId}`);
                    }
                    else {
                        setToast({ open: true, message: response.data, type: "success" });
                        // handleNavigate(true);
                    }
                }
            }
        }
        else {
            setActiveTab(1);
        }
    }

    //#region Contact form submit, filter data, show data
    // handle Contact form submit (add or edit)
    // const handleContactSubmit = async (contactFormData: any) => {
    //     if (selectedContact) {
    //         setContactDataList((prevList) => {
    //             const updatedList = prevList.map((contact) =>
    //                 contact.contactId === selectedContact.contactId
    //                     ? {
    //                         ...contact, // Keep original contact data
    //                         firstName: contactFormData.firstName,
    //                         lastName: contactFormData.lastName,
    //                         mobileNo: contactFormData.mobileNo,
    //                         office: contactFormData.office,
    //                         emailAddress: contactFormData.emailAddress,
    //                         contactType: contactFormData.contactType,
    //                         status: contactFormData.status === 'Active' ? 1 : 0, // Update based on Status
    //                         isPrimary: contactFormData.isPrimary,
    //                         updatedId: userId,
    //                     }
    //                     : contact
    //             );
    //             showGrid(updatedList); // Send updated list to showGrid
    //             return updatedList;
    //         });
    //     }
    //     else {
    //         setContactDataList((prevData) => {
    //             const maxContactId = prevData.reduce((maxId, contact) => {
    //                 return contact.contactId > maxId ? contact.contactId : maxId;
    //             }, 0);

    //             // Create the new contact with an incremented contactId
    //             const newContact: Contact = {
    //                 ...contactFormData,
    //                 contactId: maxContactId + 1,
    //                 status: contactFormData.status === 'Active' ? 1 : 0,
    //                 createdId: userId, // Assign the appropriate createdId as needed
    //             };

    //             // Return the updated contactData array with the new contact added
    //             const updatedData = [...prevData, newContact];
    //             showGrid(updatedData)
    //             return updatedData;
    //         });
    //     }
    // }

    const handleContactSubmit = async (contactFormData: any) => {
        let response: ApiResponse<any>;
        if (selectedContact) {
            const updatedContact = {
                clientId: clientId,
                contactId: selectedContact.id,
                firstName: contactFormData.firstName,
                lastName: contactFormData.lastName,
                mobileNo: contactFormData.mobileNo,
                office: contactFormData.office,
                emailAddress: contactFormData.emailAddress,
                // contactType: contactFormData.contactType,
                status: contactFormData.status === 'Active' ? 1 : 0, // Update based on Status
                isPrimary: contactFormData.isPrimary,
                updatedId: userId,
                role: contactFormData.role
            }

            response = await execute('put', '/Client/UpdateClientContact', updatedContact);
        }
        else {
            const newContact: Contact = {
                ...contactFormData,
                clientId: clientId,
                status: contactFormData.status === 'Active' ? 1 : 0,
                createdId: userId, // Assign the appropriate createdId as needed
            };

            response = await execute('post', '/Client/AddClientContact', newContact);
        }

        if (response!) {
            if (response.error) {
                // If there's an error in the response, display the error message
                setToast({ open: true, message: response.error, type: "error" });
            } else {
                // If the response is successful, display the success message
                setToast({ open: true, message: response.data, type: "success" });
                handleClose(false);
                handleContentChange(false);
                fetchAllContacts();  // Awaiting the API call
            }
        }
    }

    // filter contact Data
    const handleSearchGridData = (query: string, status: Number) => {


        const lowerCaseQuery = query.toLowerCase();

        const filtered = contactDataList.filter((item) => {
            const matchesQuery =
                item.firstName.toLowerCase().includes(lowerCaseQuery) ||
                item.lastName.toLowerCase().includes(lowerCaseQuery) ||
                item.emailAddress.toLowerCase().includes(lowerCaseQuery) ||
                item.office?.toLowerCase().includes(lowerCaseQuery);

            // const matchContactType =
            //     contactType === -1 || item.contactType === contactType;

            const matchesStatus =
                status === -1 || status === 2 || item.status === status;

            return matchesQuery
                // && matchContactType
                && matchesStatus;
        })

        const newStatus =
            status === 0
                ? [0]                          // When status is 0, set currentStatus to 'Inactive'
                : status === -1 || status === 2
                    ? [0, 1]                // When status is -1 or 2, set currentStatus to both 'Active' and 'Inactive'
                    : [1];
        setSelectedStatus(newStatus);

        showGrid(filtered, newStatus)
    }

    // show contact data
    const showGrid = (updatedData: any, status = [1]) => {
        const contactData = updatedData
            .filter((contact: any) => status.includes(contact.status))
            .map((contact: any) => {
                const name = `${contact.firstName} ${contact.lastName}`;
                // const firstLetterOfFirstName = contact.firstName?.charAt(0).toUpperCase() || '';
                // const firstLetterOfLastName = contact.lastName?.charAt(0).toUpperCase() || '';
                // // Combine both first letters
                // const shortName = `${firstLetterOfFirstName}${firstLetterOfLastName}`;
                const contactType = ContactType[contact.contactType as ContactTypeId];
                const primary = contact.isPrimary === true ? "Yes" : "No";
                const Status = contact.status === 1 ? 'Active' : 'Inactive';
                return { id: contact.contactId, conttactTypeId: contact.contactType, role: contact.role, name, email: contact.emailAddress, phone: contact.mobileNo, office: contact.office, "contact type": contactType, primary, Status }
            })
        const filteredData = contactData
            .sort((a: { name: string; email: string; }, b: { name: any; email: any; }) =>
                a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) ||
                a.email.localeCompare(b.email, undefined, { sensitivity: 'base' })
            );
        setFilteredData(filteredData)
    }
    //#endregion


    const handleNavigate = async (shouldDelay = false) => {
        if (shouldDelay) {
            await delay(2000); // Apply delay for 2 seconds if shouldDelay is true
        }
        navigate('/client/clients');
    };

    // Handle closing the modal
    const handleClose = (closeConfirmationRequired: boolean = true) => {
        if (hasUnsavedChanges && closeConfirmationRequired) {
            showConfirmCloseDialog({
                onConfirm: () => {
                    setHasUnsavedChanges(false);
                    setOpen(false);
                },
                onCancel: () => {
                },
            });
        }
        else {
            setSelectedContact(null); // Reset selectedUser when modal closes
            setOpen(false);
        }
    };

    const handleOpenModal = () => {
        setOpen(true);
    }


    const handleDialogConfirm = async () => {
        if (!selectedContact) return;

        try {
            // Make API call to update the status
            const { data } = await execute('get', `/Client/GetClientContact/${selectedContact.id}`);

            if (data) {
                // Step 2: Update the fields on the filtered record
                const updatedContact = {
                    ...data, // Keep the original user data
                    status: selectedContact.Status === 'Active' ? 0 : 1, // Update based on Status
                    updatedId: userId, // You can also update any additional fields like updatedId
                };

                let response: ApiResponse<any> = await execute('put', '/Client/UpdateClientContact', updatedContact);

                if (response!) {
                    if (response.error) {
                        // If there's an error in the response, display the error message
                        setToast({ open: true, message: response.error, type: "error" });
                    } else {
                        // If the response is successful, display the success message
                        setToast({ open: true, message: response.data, type: "success" });
                    }
                }
            }
            fetchAllContacts();
            setSelectedContact(null); // Clear selected row
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setDialogOpen(false);
        }

        // try {
        //     setContactDataList((prevList) => {
        //         const updatedList = prevList.map((contact) =>
        //             contact.contactId === selectedContact.contactId
        //                 ? {
        //                     ...contact, // Keep original contact data
        //                     status: selectedContact.Status === 'Active' ? 0 : 1, // Update based on Status
        //                     updatedId: userId,
        //                 }
        //                 : contact
        //         );
        //         showGrid(updatedList); // Send updated list to showGrid
        //         return updatedList;
        //     });
        // } finally {
        //     setDialogOpen(false);
        // }
    }

    const handleDialogCancel = () => {
        setSelectedContact(null); // Reset selectedUser when modal closes
        setDialogOpen(false);
    }

    // edit client Contact 
    const handleEdit = (contact: any) => {
        setSelectedContact(contact); // Set the contact to edit
        handleOpenModal(); // Open the modal
    };

    const handleToggleState = (row: any) => {
        if (row.Status === 'Inactive') {
            setIsOn(false)
        } else {
            setIsOn(true)
        }
        setSelectedContact(row);
        setDialogOpen(true);
    }

    // const handleNoteSubmit = async (data: any[]) => {
    //     setNotesDataList(data)
    // }

    const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const { ConfirmLeaveDialog } = useFormUnsavedChanges({
        formData,
        initialFormData,
        routesToBlock: majorRoutes
    });

    const handleContentChange = (state: boolean) => {
        setHasUnsavedChanges(state);
    };


    // client contacts
    const columns: GridColDef<(typeof filteredData)[number]>[] = [
        {
            field: 'name', headerName: 'Name', flex: 2, headerAlign: 'center',
        },
        {
            field: 'email', headerName: 'Email', flex: 2, headerAlign: 'center',
        },
        {
            field: 'phone', headerName: 'Phone', flex: 1.5, headerAlign: 'center',
        },
        {
            field: 'office', headerName: 'Office', flex: 2, headerAlign: 'center',
        },
        {
            field: 'role', headerName: 'Job Role', flex: 2, headerAlign: 'center',
        },
        {
            field: 'primary', headerName: 'Primary', flex: 1, headerAlign: 'center',
        },
        {
            field: 'Status', headerName: 'Status', flex: 1, headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || '';

                const isActive = value === 'Active';
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <Chip
                            label={value}
                            size="small"
                            sx={{
                                backgroundColor: isActive ? '#e8f5e9' : '#ffebee',
                                color: isActive ? '#2e7d32' : "#EF4444",
                                fontWeight: '600',
                                fontSize: '11px',
                                border: `1px solid ${isActive ? '#a5d6a7' : '#ef9a9a'}`,
                                height: "24px",
                            }}
                        />
                    </Box>
                );
            }
        },
        {
            field: 'actions', headerName: 'Action', flex: 1, headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, width: '100%' }}>
                    <IconButton
                        color="primary"
                        aria-label="edit"
                        onClick={() => {
                            handleEdit(params.row);
                        }}
                    >
                        <EditCalendarIcon sx={{ color: '#a3c63d' }} ></EditCalendarIcon>
                    </IconButton>

                    <CustomToggleButton
                        isOn={params.row.Status === 'Active'}
                        onClick={() => handleToggleState(params.row)}
                    >
                    </CustomToggleButton>
                </div>
            ),
        },
    ];

    return (
        <AppLayout>
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
                <Grid2 container spacing={2} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Grid2 size={{ xs: 12, md: 6, lg: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton sx={{ width: '40px', height: '40px', backgroundColor: 'transparent' }} onClick={() => handleNavigate()} >
                            <ArrowBackIosIcon sx={{ color: '#1e66ab', fontSize: '15px', fontWeight: 500 }} />
                        </IconButton>

                        <Typography variant="h6" noWrap sx={headingStyles}>
                            {clientId ? 'Edit Client' : 'Add Client'}
                        </Typography>
                    </Grid2>
                    <Grid2 container spacing={1} size={{ xs: 12, md: 6, lg: 6 }} justifyContent="flex-end">
                        {clientId && (
                            <>
                                <Grid2 size={{ xs: 12, md: 6, lg: 4, }}>
                                    <CustomButton
                                        capsule
                                        sx={{
                                            mt: 0,
                                            mb: 2,
                                            textTransform: 'none',
                                            whiteSpace: 'nowrap'
                                        }}
                                        backgroundColor="#1e66ab"
                                        hoverColor="rgba(150, 193, 33, 0.75)"
                                        onClick={() => navigate(`/project/add-projects?clientId=${clientId}`)}
                                    >
                                        Add Project
                                    </CustomButton>
                                </Grid2>
                            </>
                        )}

                        <Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
                            <CustomButton
                                capsule
                                sx={{
                                    mt: 0,
                                    mb: 2,
                                    whiteSpace: 'nowrap',
                                    textTransform: 'none'
                                }}
                                onClick={handleClientSubmit}
                            >
                                {clientId ? 'Update Client' : 'Save Client'}
                            </CustomButton>
                        </Grid2>
                    </Grid2>
                </Grid2>
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
                    <Tabs style={{ width: "65%", fontSize: "16px", position: "relative", left: "0%" }}
                        value={activeTab}
                        onChange={handleChangeTab}
                        aria-label="secondary tabs example"
                    >
                        <Tab value={1} label="General" sx={tabStyles} />
                        <Tab value={2} label="Notes/Comments" sx={tabStyles} disabled={!clientId ? true : false} />
                        <Tab value={3} label="View Project" sx={tabStyles} disabled={!clientId ? true : false} />

                    </Tabs>

                    <Box sx={{
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        // height: "calc(100vh - 190px)",
                        paddingRight: 2,
                    }}>

                        {activeTab === 2 ? (
                            <ClientNotes clientId={Number(clientId)} />
                        ) : activeTab === 3 ? (
                            <ViewClientProjects
                                clientName={formData.clientName}
                                projectData={projectData}
                            />
                        ) :
                            (
                                <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 0, overflowY: "auto", pr: 2 }}>
                                    <Grid2 container spacing={2} sx={{ mb: clientId ? 0 : 7, mt: 2, backgroundColor: '#f7faff', border: '1px solid #e0eaf6', borderRadius: '14px', padding: 1.5, marginBottom: 1 }}>
                                        <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                                            <CustomTextField
                                                label="Client Number"
                                                name="clientNumber"
                                                value={formData.clientNumber}
                                                onChange={handleChange}
                                                sx={{ mb: 1, backgroundColor: '#fff' }}
                                                required={true}
                                                disable={clientId ? true : false}
                                            />
                                            {errors.clientNumber && (
                                                <Typography className="validationMessage" role="alert">
                                                    {errors.clientNumber}
                                                </Typography>
                                            )}
                                        </Grid2>

                                        <Grid2 size={{ xs: 12, md: 6 }}>
                                            <CustomTextField
                                                label="Client Name"
                                                name="clientName"
                                                value={formData.clientName}
                                                onChange={handleChange}
                                                sx={{ mb: 1, backgroundColor: '#fff' }}
                                                required={true}
                                            />
                                            {errors.clientName && (
                                                <Typography className="validationMessage" role="alert">
                                                    {errors.clientName}
                                                </Typography>
                                            )}
                                        </Grid2>

                                        <Grid2 size={{ xs: 12, md: 6 }}>
                                            <CustomDropdown
                                                value={formData.clientCategory}
                                                onChange={(event) => setFormData({ ...formData, clientCategory: Number(event.target.value) })}
                                                label="Client Category"
                                                options={category}
                                                requiredLabel={true}
                                                sx={{ mb: 1, height: '35px', fontSize: '12px', backgroundColor: '#fff' }}
                                            />
                                            {errors.clientCategory && (
                                                <Typography className="validationMessage" role="alert">
                                                    {errors.clientCategory}
                                                </Typography>
                                            )}
                                        </Grid2>

                                        <Grid2 size={{ xs: 12, md: 6 }}>
                                            <CustomDropdown
                                                value={formData.clientRegion}
                                                onChange={(event) => setFormData({ ...formData, clientRegion: Number(event.target.value) })}
                                                label="Client Region"
                                                options={region}
                                                requiredLabel={true}
                                                sx={{ mb: 1, height: '35px', fontSize: '12px', backgroundColor: '#fff' }}
                                            />
                                            {errors.clientRegion && (
                                                <Typography className="validationMessage" role="alert">
                                                    {errors.clientRegion}
                                                </Typography>
                                            )}
                                        </Grid2>

                                        {clientId && (
                                            <>   <Grid2 size={{ xs: 12, md: 12, lg: 12 }}>
                                                <Typography sx={{ color: '#1e66ab', fontSize: 14, fontWeight: 500, ml: 1, mt: 1 }}>Status</Typography>
                                                <Grid2 sx={{ display: 'flex' }}> {/* Adjust gap as needed */}
                                                    {/* Custom Radio Buttons */}
                                                    <CustomRadioButton
                                                        label="Active"
                                                        value="Active"
                                                        name="status"
                                                        checked={formData.status === 'Active'}
                                                        onChange={handleChange}
                                                        required={true}
                                                    />
                                                    <CustomRadioButton
                                                        label="Inactive"
                                                        value="Inactive"
                                                        name="status"
                                                        checked={formData.status === 'Inactive'}
                                                        onChange={handleChange}
                                                        required={true}
                                                    />
                                                </Grid2>
                                            </Grid2>

                                            </>
                                        )}
                                    </Grid2>



                                    {/* {contactDataList.length > 0 && ( */}
                                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                        <Box sx={{ pb: 3 }}>
                                            <FilterCard {...props}
                                                handleModalOrNavigate={() => handleOpenModal()}
                                                handleSearch={handleSearchGridData}
                                            />
                                        </Box>

                                        <CustomAddContactModal
                                            handleContentChange={handleContentChange}
                                            open={open}
                                            onClose={handleClose}
                                            onSubmit={handleContactSubmit}
                                            contactData={selectedContact}
                                        />

                                        {filteredData.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: "auto" }}>
                                                <CustomDataGrid
                                                    rows={filteredData}
                                                    columns={columns}
                                                    isDialogOpen={dialogOpen}
                                                    isToggleOn={isOn}
                                                    actionName={'Contact'}
                                                    handleCancel={handleDialogCancel}
                                                    handleConfirm={handleDialogConfirm}
                                                />
                                            </Box>
                                        ) : (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center', // horizontal center
                                                    alignItems: 'center',     // vertical center
                                                    height: '400px',          // adjust based on container
                                                    color: 'rgb(30, 102, 171)',
                                                    fontSize: '16px',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                No client contact found matching the search criteria
                                            </Box>
                                        )}
                                    </Box>
                                    {/* )} */}
                                </Box>
                            )}

                    </Box>
                </Box>
            </Box>

            <Toaster open={toast.open} message={toast.message} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />
            <ConfirmLeaveDialog />
        </AppLayout>
    );
}

export default AddClient;
